/**
 * DashboardFiltersBar — barra filtri della Dashboard (periodo, granularità,
 * date-mode, filtro agente, export report, refresh manuale/auto, banner range).
 * ★ Fase 59.P2 — estratto da DashboardPage.jsx. Comportamento e testid invariati.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import DateModeSelector from '../DateModeSelector';
import {
  Download, CalendarRange, Filter as FilterIcon, FileSpreadsheet,
  FileText as FilePdfIcon, RefreshCw, Zap,
} from 'lucide-react';
import { QUICK_RANGES } from './dashboardUtils';

export function DashboardFiltersBar({
  startDate, setStartDate, endDate, setEndDate,
  granularity, setGranularity,
  dateMode, setDateMode,
  agentFilter, setAgentFilter, agents, isAdminLike,
  canExport, downloading, downloadReport,
  setQuickRange, selectedAgentLabel,
  lastRefreshAt, loading, handleManualRefresh,
  autoRefresh, toggleAutoRefresh,
}) {
  const { t } = useTranslation();
  return (
    <Card className="border-border">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-muted-foreground mr-2">
            <CalendarRange className="w-4 h-4" /> {t('dashboard.period', 'Periodo:')}
          </div>
          {QUICK_RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => setQuickRange(r)}
              data-testid={`quick-range-${r.key}`}
            >
              {t(`dashboard.range.${r.key}`, r.label)}
            </Button>
          ))}

          <div className="h-6 w-px bg-border mx-1" />

          <Label className="text-xs text-muted-foreground">{t('dashboard.dateFrom', 'Da')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-8 w-36 text-xs"
            data-testid="dashboard-start-date"
          />
          <Label className="text-xs text-muted-foreground">{t('dashboard.dateTo', 'a')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-8 w-36 text-xs"
            data-testid="dashboard-end-date"
          />

          <div className="h-6 w-px bg-border mx-1" />

          <DateModeSelector
            value={dateMode}
            onChange={setDateMode}
            size="sm"
            testIdPrefix="dashboard-date-mode"
          />

          <div className="h-6 w-px bg-border mx-1" />

          <Label className="text-xs text-muted-foreground">{t('dashboard.granularityLabel', 'Granularità')}</Label>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="dashboard-granularity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">{t('dashboard.granularityOptions.daily', 'Giornaliera')}</SelectItem>
              <SelectItem value="weekly">{t('dashboard.granularityOptions.weekly', 'Settimanale')}</SelectItem>
              <SelectItem value="monthly">{t('dashboard.granularityOptions.monthly', 'Mensile')}</SelectItem>
            </SelectContent>
          </Select>

          {isAdminLike && (
            <>
              <div className="h-6 w-px bg-border mx-1" />
              <FilterIcon className="w-3.5 h-3.5 text-muted-foreground" />
              <Select value={agentFilter || 'all'} onValueChange={(v) => setAgentFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-48 text-xs" data-testid="dashboard-agent-filter">
                  <SelectValue placeholder={t('dashboard.allAgents', 'Tutti gli agenti')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('dashboard.allAgents', 'Tutti gli agenti')}</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          {canExport && (
            <>
              <div className="ml-auto" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="h-8 text-xs" disabled={downloading} data-testid="dashboard-download-trigger">
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    {downloading ? t('dashboard.downloading', 'Scaricando…') : t('dashboard.downloadReport', 'Scarica report')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => downloadReport('xlsx')} data-testid="download-xlsx">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadReport('pdf')} data-testid="download-pdf">
                    <FilePdfIcon className="w-3.5 h-3.5 mr-2 text-red-600" /> PDF (.pdf)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>

        {/* ★ 2026-07-03: Banner "Range attivo" — chiaro riepilogo del periodo filtrato */}
        <div
          className="mt-2 pt-2 border-t border-border flex items-center justify-between text-[11px] flex-wrap gap-2"
          data-testid="dashboard-active-range-banner"
        >
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarRange className="w-3 h-3" />
            <span>{t('dashboard.activeRange', 'Periodo attivo:')}</span>
            <b className="text-foreground" data-testid="active-range-start">{startDate}</b>
            <span>→</span>
            <b className="text-foreground" data-testid="active-range-end">{endDate}</b>
            <span className="text-muted-foreground">·</span>
            <span>{granularity === 'daily' ? t('dashboard.granularityOptions.daily', 'Giornaliera').toLowerCase() : granularity === 'weekly' ? t('dashboard.granularityOptions.weekly', 'Settimanale').toLowerCase() : t('dashboard.granularityOptions.monthly', 'Mensile').toLowerCase()}</span>
            {selectedAgentLabel && (
              <>
                <span className="text-muted-foreground">·</span>
                <span>Agente: <b className="text-foreground">{selectedAgentLabel}</b></span>
              </>
            )}
          </div>

          {/* ★ 2026-07-03: Refresh controls */}
          <div className="flex items-center gap-2">
            {lastRefreshAt && (
              <span className="text-[10px] text-muted-foreground" data-testid="last-refresh-at">
                {t('dashboard.updatedAt', 'Aggiornato alle')} {lastRefreshAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            {loading && (
              <span className="text-[10px] text-blue-600 animate-pulse">{t('dashboard.recalc', 'Ricalcolo…')}</span>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-[11px] px-2 gap-1"
              onClick={handleManualRefresh}
              disabled={loading}
              data-testid="dashboard-manual-refresh"
              title={t('dashboard.refreshNowTitle', 'Ricarica ora i KPI e le proposte')}
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Aggiorna')}
            </Button>
            <Button
              size="sm"
              variant={autoRefresh ? 'default' : 'outline'}
              className={`h-6 text-[11px] px-2 gap-1 ${autoRefresh ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={toggleAutoRefresh}
              data-testid="dashboard-auto-refresh-toggle"
              title={t('dashboard.autoRefreshTitle', 'Ricarica automaticamente ogni 60 secondi')}
            >
              <Zap className="w-3 h-3" />
              {autoRefresh ? 'Auto ON' : 'Auto OFF'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
